import CloseIcon from "@mui/icons-material/Close";
import {
	Box,
	Button,
	Checkbox,
	Dialog,
	DialogContent,
	DialogTitle,
	IconButton,
	Stack,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	Typography,
} from "@mui/material";
import { useEffect, useRef, useState } from "react";
import { LoadingComp } from "../../../../shared/components/loading/Loading";
import { useToast } from "../../../../shared/hooks";
import { ICollectionPendingValue, SinisterService } from "../../../../shared/services/api/sinister/SinisterService";
import { formatDateFnsRemoveTimezone } from "../../../../shared/utils/format-date";
import { formatScaleValueBR } from "../../../../shared/utils/formatScaleValue";

interface Props {
	open: boolean;
	sinisterId: string;
	onClose: () => void;
	onConfirmed: () => void;
}

// Mesmas opções/validação de arquivo já usadas em ModalAddValues — ver comentário equivalente em
// ModalLaunchFutureValue.tsx sobre não extrair uma constante compartilhada fora do escopo deste PR.
const SINISTER_VALUE_TYPE_OPTIONS: Array<{ value: string; label: string }> = [
	{ value: "rent_values", label: "Aluguel" },
	{ value: "water_values", label: "Água" },
	{ value: "fire_insurance", label: "Apólice seguro incêndio" },
	{ value: "condominium_values", label: "Condomínio" },
	{ value: "damage", label: "Danos ao Imóvel" },
	{ value: "energy_values", label: "Energia Elétrica" },
	{ value: "iptu", label: "IPTU" },
	{ value: "gas_values", label: "Gás" },
	{ value: "cleaning", label: "Limpeza" },
	{ value: "fine_on_rent", label: "Multa sobre Aluguel" },
	{ value: "fine_on_condominium", label: "Multa sobre valor do condomínio" },
	{ value: "painting", label: "Pintura" },
	{ value: "garantti_tax", label: "Taxa mensal Garantti" },
	{ value: "other", label: "Outros" },
];

const getTypeLabel = (typeId: string) => SINISTER_VALUE_TYPE_OPTIONS.find((option) => option.value === typeId)?.label ?? typeId;

const allowedFileSize = 50 * 1024 * 1024;
const allowedMimetypes = [
	"application/pdf",
	"application/msword",
	"application/vnd.openxmlformats-officedocument.wordprocessingml.document",
	"application/vnd.oasis.opendocument.text",
	"image/jpeg",
	"image/jpg",
	"image/png",
];

const validateAttachment = (file: File): string | undefined => {
	if (file.size > allowedFileSize) return `Tamanho do arquivo excede ${(allowedFileSize / (1024 * 1024)).toFixed(0)}MB`;
	if (!allowedMimetypes.includes(file.type)) return "Tipo de arquivo inválido";
	return undefined;
};

export const ModalConfirmCollectionValues = ({ open, sinisterId, onClose, onConfirmed }: Props) => {
	const toastHandler = useToast();
	const [pending, setPending] = useState<ICollectionPendingValue[]>([]);
	const [isLoading, setIsLoading] = useState(false);
	const [fileByPendingId, setFileByPendingId] = useState<Record<string, File>>({});
	const [confirmingId, setConfirmingId] = useState<string | null>(null);
	const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
	const [isBulkConfirming, setIsBulkConfirming] = useState(false);
	const fileInputRefs = useRef<Record<string, HTMLInputElement | null>>({});

	const fetchPending = () => {
		setIsLoading(true);
		SinisterService.getCollectionPendingValues(sinisterId).then((result) => {
			if (!(result instanceof Error)) setPending(result);
			setSelectedIds(new Set());
			setIsLoading(false);
		});
	};

	useEffect(() => {
		if (open) fetchPending();
	}, [open, sinisterId]);

	const areAllSelected = pending.length > 0 && selectedIds.size === pending.length;
	const areSomeSelected = selectedIds.size > 0 && !areAllSelected;

	const toggleSelection = (pendingId: string) => {
		setSelectedIds((prev) => {
			const next = new Set(prev);
			if (next.has(pendingId)) next.delete(pendingId);
			else next.add(pendingId);
			return next;
		});
	};

	const toggleSelectAll = () => {
		setSelectedIds((prev) => (prev.size === pending.length ? new Set() : new Set(pending.map((item) => item.id))));
	};

	const handlePickFile = (pendingId: string, file: File | undefined) => {
		if (!file) return;
		const error = validateAttachment(file);
		if (error) {
			toastHandler.present({ type: "error", messageOrContent: error, position: "top-center" });
			return;
		}
		setFileByPendingId((prev) => ({ ...prev, [pendingId]: file }));
	};

	const handleConfirm = async (item: ICollectionPendingValue) => {
		setConfirmingId(item.id);
		const result = await SinisterService.confirmCollectionPendingValue(
			item.id,
			{ typeId: item.typeId, value: item.value, dueDate: item.dueDate, reference: item.reference },
			fileByPendingId[item.id]
		);
		setConfirmingId(null);

		if (result instanceof Error) {
			toastHandler.present({ type: "error", messageOrContent: result.message, position: "top-right" });
			return;
		}

		toastHandler.present({ type: "success", messageOrContent: "Valor confirmado com sucesso!", position: "top-right" });
		setPending((prev) => prev.filter((pendingItem) => pendingItem.id !== item.id));
		setFileByPendingId((prev) => {
			const { [item.id]: _removed, ...rest } = prev;
			return rest;
		});
		setSelectedIds((prev) => {
			const next = new Set(prev);
			next.delete(item.id);
			return next;
		});
		onConfirmed();
	};

	const handleConfirmSelected = async () => {
		const items = pending.filter((item) => selectedIds.has(item.id));
		if (items.length === 0) return;

		setIsBulkConfirming(true);
		const results = await Promise.all(
			items.map((item) =>
				SinisterService.confirmCollectionPendingValue(
					item.id,
					{ typeId: item.typeId, value: item.value, dueDate: item.dueDate, reference: item.reference },
					fileByPendingId[item.id]
				)
			)
		);
		setIsBulkConfirming(false);

		const confirmedIds = new Set(items.filter((_, index) => !(results[index] instanceof Error)).map((item) => item.id));
		const failures = results.filter((result) => result instanceof Error) as Error[];

		if (confirmedIds.size > 0) {
			setPending((prev) => prev.filter((item) => !confirmedIds.has(item.id)));
			setFileByPendingId((prev) => {
				const rest = { ...prev };
				confirmedIds.forEach((id) => delete rest[id]);
				return rest;
			});
			setSelectedIds((prev) => {
				const next = new Set(prev);
				confirmedIds.forEach((id) => next.delete(id));
				return next;
			});
		}

		if (confirmedIds.size > 0) {
			toastHandler.present({
				type: failures.length > 0 ? "warning" : "success",
				messageOrContent:
					failures.length > 0
						? `${confirmedIds.size} valor(es) confirmado(s). ${failures.length} falharam.`
						: `${confirmedIds.size} valor(es) confirmado(s) com sucesso!`,
				position: "top-right",
			});
		} else if (failures.length > 0) {
			toastHandler.present({ type: "error", messageOrContent: failures[0].message, position: "top-right" });
		}

		onConfirmed();
	};

	return (
		<Dialog open={open} onClose={onClose} maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: 3, overflow: "hidden" } }}>
			<LoadingComp isopen={isLoading || isBulkConfirming} />
			<DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
				<Typography variant="h6" color="primary" sx={{ fontWeight: 400 }}>
					Confirmar Valores da Cobrança
				</Typography>
				<IconButton onClick={onClose} size="small">
					<CloseIcon fontSize="small" />
				</IconButton>
			</DialogTitle>

			<DialogContent dividers>
				{pending.length === 0 ? (
					<Typography variant="body2" color="text.secondary">
						Nenhum valor futuro pendente de confirmação pra esse sinistro.
					</Typography>
				) : (
					<TableContainer>
						<Table size="small">
							<TableHead>
								<TableRow>
									<TableCell padding="checkbox">
										<Checkbox
											size="small"
											checked={areAllSelected}
											indeterminate={areSomeSelected}
											onChange={toggleSelectAll}
											inputProps={{ "aria-label": "Selecionar todos os valores" }}
										/>
									</TableCell>
									<TableCell>Tipo</TableCell>
									<TableCell>Referência</TableCell>
									<TableCell align="center">Vencimento</TableCell>
									<TableCell align="right">Valor</TableCell>
									<TableCell>Motivo</TableCell>
									<TableCell>Lançado por</TableCell>
									<TableCell align="center">Comprovante</TableCell>
									<TableCell align="center">Ação</TableCell>
								</TableRow>
							</TableHead>
							<TableBody>
								{pending.map((item) => {
									const file = fileByPendingId[item.id];
									return (
										<TableRow key={item.id} selected={selectedIds.has(item.id)}>
											<TableCell padding="checkbox">
												<Checkbox
													size="small"
													checked={selectedIds.has(item.id)}
													onChange={() => toggleSelection(item.id)}
													inputProps={{ "aria-label": `Selecionar ${getTypeLabel(item.typeId)}` }}
												/>
											</TableCell>
											<TableCell>{getTypeLabel(item.typeId)}</TableCell>
											<TableCell>{item.reference}</TableCell>
											<TableCell align="center">{formatDateFnsRemoveTimezone(item.dueDate) || "-"}</TableCell>
											<TableCell align="right">{formatScaleValueBR(item.value)}</TableCell>
											<TableCell>
												<Typography variant="body2" sx={{ maxWidth: 220 }} noWrap title={item.reason}>
													{item.reason}
												</Typography>
											</TableCell>
											<TableCell>{item.createdByUserName}</TableCell>
											<TableCell align="center">
												<Stack spacing={0.5} alignItems="center">
													<Button
														size="small"
														variant={file ? "outlined" : "text"}
														onClick={() => fileInputRefs.current[item.id]?.click()}
													>
														{file ? "Trocar" : "Anexar (opcional)"}
													</Button>
													<input
														ref={(el) => (fileInputRefs.current[item.id] = el)}
														type="file"
														style={{ display: "none" }}
														onChange={(e) => handlePickFile(item.id, e.target.files?.[0])}
													/>
													{file && (
														<Typography variant="caption" color="text.secondary" noWrap sx={{ maxWidth: 140 }}>
															{file.name}
														</Typography>
													)}
												</Stack>
											</TableCell>
											<TableCell align="center">
												<Button
													size="small"
													variant="contained"
													disabled={confirmingId === item.id || isBulkConfirming}
													onClick={() => handleConfirm(item)}
												>
													Confirmar
												</Button>
											</TableCell>
										</TableRow>
									);
								})}
							</TableBody>
						</Table>
					</TableContainer>
				)}
			</DialogContent>

			<Box sx={{ px: 3, py: 2, display: "flex", justifyContent: "flex-end", gap: 1 }}>
				<Button onClick={onClose}>Fechar</Button>
				<Box flex={1} />
				<Button
					variant="contained"
					disabled={selectedIds.size === 0 || isBulkConfirming || confirmingId !== null}
					onClick={handleConfirmSelected}
				>
					Confirmar Selecionados {selectedIds.size > 0 ? `(${selectedIds.size})` : ""}
				</Button>
			</Box>
		</Dialog>
	);
};
